import React, { Fragment } from 'react';
import styled from 'styled-components';

import { Translation, useTranslation } from '~/components/Translation';
import {
	CTARow,
	FaqBlock,
	ImageBlock,
	Image640x560,
	MultipleSrcPicture,
	Signup,
	Header,
} from '~/components/Marketing';
import ResponsiveText from '~/components/ResponsiveText';
import LinkBody from '~/components/LinkBodyV2';
import analytics from '~/services/analytics';
import usePageTrack from '~/services/analytics/usePageTrack';
import { media, visibility } from '~/styles/mixins';
import featureFlags from '~/constants/featureFlags';

import HeroImageLargePng from './hero_image_large.png';
import HeroImageLargeWebp from './hero_image_large.webp';
import HeroImageSmallPng from './hero_image_small.png';
import HeroImageSmallWebp from './hero_image_small.webp';

import BlockImageInviteGuestsPng from './block_image_invite_guests.png';
import BlockImageInviteGuestsWebp from './block_image_invite_guests.webp';
import BlockImageGuestInterviewsPng from './block_image_guest_interviews.png';
import BlockImageGuestInterviewsWebp from './block_image_guest_interviews.webp';
import BlockImagePodcastsPng from './block_image_podcasts.png';
import BlockImagePodcastsWebp from './block_image_podcasts.webp';
import MobileLandingSignup from '../../../Signup/scenes/MobileLandingSignup';

const HeroImageLarge = styled.div`
	width: 100%;
	max-width: 1400px;
	margin: 48px auto;
	${visibility.showMedium}
`;

const HeroImageLargeImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const HeroImageSmall = styled.div`
	width: 100%;
	max-width: 500px;
	margin: 16px auto;
	padding: 0 24px;
	${media.medium`
    	padding: 0 80px;
  	`}
	${visibility.hideMedium}
`;

const HeroImageSmallImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const faqList = [
	{
		question: <Translation tid="landingGuestInterviews.faq.question.one" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.one" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingGuestInterviews.faq.question.two" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.two" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingGuestInterviews.faq.question.three" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.three" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingGuestInterviews.faq.question.four" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.four" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingGuestInterviews.faq.question.five" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.five" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingGuestInterviews.faq.question.six" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.six" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingGuestInterviews.faq.question.seven" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingGuestInterviews.faq.answer.seven" />
				</ResponsiveText>
			</Fragment>
		),
	},
];

const GuestInterviews = () => {
	usePageTrack({
		name: analytics.events.GUEST_INTERVIEWS,
		category: analytics.events.category.MARKETING,
	});

	return (
		<main role="main">
			<Header
				title={<Translation tid="landingGuestInterviews.title" />}
				subtitles={[
					<Translation key="1" tid="landingGuestInterviews.subtitle" />,
				]}
				cta={<Signup />}
				redesign={featureFlags.orion.landingPageRedesignV2}
			/>
			<HeroImageLarge>
				<HeroImageLargeImage
					fallbackSrc={HeroImageLargePng}
					webpSrc={HeroImageLargeWebp}
					alt=""
				/>
			</HeroImageLarge>
			<HeroImageSmall>
				<HeroImageSmallImage
					fallbackSrc={HeroImageSmallPng}
					webpSrc={HeroImageSmallWebp}
					alt=""
				/>
			</HeroImageSmall>
			<MobileLandingSignup />
			<ImageBlock
				body={useTranslation(
					'landingGuestInterviews.block.hostConversations.body',
				)}
				header={useTranslation(
					'landingGuestInterviews.block.hostConversations.header',
				)}
				image={
					<Image640x560
						webpSrc={BlockImageInviteGuestsWebp}
						fallbackSrc={BlockImageInviteGuestsPng}
						alt=""
					/>
				}
			/>
			<ImageBlock
				body={useTranslation('landingGuestInterviews.block.multistream.body')}
				color="slate"
				header={useTranslation(
					'landingGuestInterviews.block.multistream.header',
				)}
				image={
					<Image640x560
						webpSrc={BlockImageGuestInterviewsWebp}
						fallbackSrc={BlockImageGuestInterviewsPng}
						alt=""
					/>
				}
				reverse={true}
			/>
			<ImageBlock
				body={useTranslation('landingGuestInterviews.block.podcast.body')}
				header={useTranslation('landingGuestInterviews.block.podcast.header')}
				image={
					<Image640x560
						webpSrc={BlockImagePodcastsWebp}
						fallbackSrc={BlockImagePodcastsPng}
						alt=""
					/>
				}
			/>
			<FaqBlock
				faqList={faqList}
				header={<Translation tid="landingGuestInterviews.faq.header" />}
			/>
			<CTARow
				body={
					<Translation
						tid="landingGuestInterviews.cta.body"
						components={{
							link: <LinkBody to="/contact-us" color="onPrimary" />,
						}}
					/>
				}
				cta={<Signup />}
				subtitle={<Translation tid="landingGuestInterviews.cta.subtitle" />}
				title={<Translation tid="landingGuestInterviews.cta.title" />}
			/>
		</main>
	);
};

export default GuestInterviews;
